import { jsx as _jsx } from "react/jsx-runtime";
import { ModalContext } from '#pplx/frontend/aether/components/internal/surface-content/ModalContext.js';
import { PopoverContext } from '#pplx/frontend/aether/components/internal/surface-content/PopoverContext.js';
import { desktopContentVariants, getBottomSheetContentClassName, getPanelPaddingClassName, getSideSheetContentClassName, } from '#pplx/frontend/aether/components/Modal/internal/ModalInternals.js';
import { ModalLayoutProvider } from '#pplx/frontend/aether/components/Modal/internal/ModalLayoutContext.js';
import { Surface } from '#pplx/frontend/aether/components/Surface/index.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import * as Dialog from '@radix-ui/react-dialog';
import { animated, SpringValue } from '@react-spring/web';
import classNames from 'classnames';
import { useMemo, useRef } from 'react';
function getContentMotionStyle(props) {
    switch (props.variant) {
        case 'dialog':
            if (props.animation === 'fade') {
                return { willChange: 'opacity', opacity: props.opacity };
            }
            return {
                willChange: 'opacity, transform',
                opacity: props.opacity,
                transform: props.scale.to((scale) => `scale(${scale})`),
            };
        case 'side-sheet':
            return { willChange: 'transform', transform: props.x.to((x) => `translateX(${x}%)`) };
        case 'bottom-sheet':
            return { willChange: 'transform', transform: props.y.to((y) => `translateY(${y}%)`) };
        default:
            return assertUnreachableValue(props);
    }
}
export function ModalContent({ background, dismissOnOutsideClick, contentStyle, contentAriaProps, layoutState, children, ...props }) {
    const panelRef = useRef(null);
    const contentHandle = useMemo(() => ({
        getContentBounds: () => {
            const rect = panelRef.current?.getBoundingClientRect();
            return rect ? { bottomPx: rect.bottom, leftPx: rect.left, widthPx: rect.width } : null;
        },
        registerOnContentResize: (onResize) => {
            if (!panelRef.current || typeof ResizeObserver === 'undefined')
                return () => { };
            const observer = new ResizeObserver(onResize);
            observer.observe(panelRef.current);
            return () => observer.disconnect();
        },
    }), []);
    return (_jsx(ModalContext.Provider, { value: contentHandle, children: _jsx(Dialog.Content, { asChild: true, forceMount: true, ...contentAriaProps, onPointerDownOutside: (event) => {
                // Suppress Radix's default backdrop dismissal for explicit confirmation flows.
                if (!dismissOnOutsideClick)
                    event.preventDefault();
            }, children: _jsx(animated.div, { ref: panelRef, className: classNames(props.variant === 'dialog'
                    ? desktopContentVariants({ size: props.size, background })
                    : props.variant === 'side-sheet'
                        ? getSideSheetContentClassName({ background })
                        : getBottomSheetContentClassName({ background }), 
                // Padding lives once at the panel level. Subcomponents
                // (`ModalHeader` / `ModalBody` / `ModalFooter`) intentionally don't
                // apply their own — see those files. Skipped for chromeless modals.
                getPanelPaddingClassName({
                    chromeless: layoutState.chromeless,
                    isMobile: props.variant === 'bottom-sheet',
                }), 'outline-hidden'), style: {
                    ...contentStyle,
                    ...getContentMotionStyle(props),
                }, children: _jsx(Surface, { className: "contents", children: _jsx(PopoverContext.Provider, { value: null, children: _jsx(ModalLayoutProvider, { value: layoutState, children: children }) }) }) }) }) }));
}
//# sourceMappingURL=ModalContent.js.map